import {useCallback, useEffect} from "react";
import {useSignal} from "@preact/signals-react";
import {postSubmit} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";


export default function useRawMatcherBrandSkus(brandID, siteID, excluded) {
	const isLoading = useSignal(false)
	const searchText = useSignal('')
	const skuOptions = useSignal([])

	// AbortController of the latest in-flight request; only the most recent request may mutate
	// state, so a late-resolving superseded/aborted request can't overwrite the new selection.
	const controller = useSignal(null)

	useEffect(() => {
		return () => {
			if (controller.value) {
				controller.value.abort()
				controller.value = null
			}
		}
	}, [])

	const loadBrandSkus = useCallback(() => {
		if (!brandID || !siteID) return

		if (controller.value) controller.value.abort()
		const myController = new AbortController()
		controller.value = myController

		isLoading.value = true
		postSubmit(`${BACKEND_APIS.MATCHERS}/selectors/skus-by-brand/${brandID}`, {
			find: searchText.value,
			// Sent so each option knows whether its sku has a url on this site, and the name is
			// only offered as a link when it does.
			siteID: siteID
		}, { signal: myController.signal }).then(res => {
			if (controller.value !== myController) return
			controller.value = null
			isLoading.value = false
			skuOptions.value = res.options
		}).catch(() => {
			if (controller.value !== myController) return
			controller.value = null
			isLoading.value = false
		})
	}, [brandID, siteID, searchText.value])

	// Clear options when the brand/site changes so the previous selection's options don't linger.
	useEffect(() => {
		skuOptions.value = []
	}, [brandID, siteID])

	useEffect(() => {
		loadBrandSkus()
	}, [brandID, siteID, searchText.value]);

	const handleChangeSearchText = useCallback(newValue => {
		searchText.value = newValue
	}, [])

	return {
		brandOptions: skuOptions.value.filter(op => searchText.value ? true : !excluded.includes(op.skuID)),
		isLoading: isLoading.value,
		searchText: searchText.value,

		handleChangeSearchText
	}
}
